import { PERMISSIONS } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Input, Modal, Table, Tag, Typography } from 'antd';
import { useState } from 'react';
import { api, errorText } from '../../api/client';
import type { Setting } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';

/** Cấu hình dạng chữ: sửa như văn bản thường (không cần JSON), để trống = chưa có */
const TEXT_PREFIXES = ['quotation.default_terms', 'company.', 'contact.', 'privacy.'];
const isText = (s: Setting) =>
  typeof s.value === 'string' || TEXT_PREFIXES.some((p) => s.key.startsWith(p));

/** Cấu hình hệ thống (VAT, quy đổi kỳ thuê, hotline…). Giá trị số/danh sách nhập dạng JSON, máy chủ kiểm tra kiểu. */
export function SettingsPage() {
  const { can } = useAuth();
  const canEdit = can(PERMISSIONS.SETTINGS_MANAGE);
  const { message } = App.useApp();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Setting[]>('/admin/settings'),
  });
  const [editing, setEditing] = useState<Setting | null>(null);
  const [text, setText] = useState('');

  const save = async () => {
    if (!editing) return;
    let value: unknown;
    try {
      value = isText(editing) ? (text.trim() === '' ? null : text.trim()) : JSON.parse(text);
    } catch {
      message.error(
        'Giá trị phải đúng định dạng JSON (chuỗi đặt trong dấu ngoặc kép, số không cần)',
      );
      return;
    }
    try {
      await api.put(`/admin/settings/${encodeURIComponent(editing.key)}`, { value });
      message.success('Đã lưu');
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['settings'] });
    } catch (e) {
      message.error(errorText(e));
    }
  };

  return (
    <Card title="Cấu hình hệ thống">
      <Table<Setting>
        rowKey="key"
        pagination={false}
        loading={list.isLoading}
        dataSource={list.data}
        columns={[
          {
            title: 'Khóa',
            dataIndex: 'key',
            render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
          },
          { title: 'Mô tả', dataIndex: 'description' },
          {
            title: 'Giá trị',
            render: (_, r) =>
              r.value === null ? (
                <Tag color="orange">Chưa xác nhận</Tag>
              ) : typeof r.value === 'string' ? (
                <Typography.Paragraph
                  style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginBottom: 0, maxWidth: 520 }}
                  ellipsis={{ rows: 3, expandable: true, symbol: 'xem thêm' }}
                >
                  {r.value}
                </Typography.Paragraph>
              ) : (
                <Typography.Text style={{ fontSize: 12 }}>
                  {JSON.stringify(r.value)}
                </Typography.Text>
              ),
          },
          { title: '', render: (_, r) => (r.is_public ? <Tag color="blue">Công khai</Tag> : null) },
          ...(canEdit
            ? [
                {
                  title: '',
                  render: (_: unknown, r: Setting) => (
                    <Button
                      size="small"
                      onClick={() => (
                        setEditing(r),
                        setText(
                          isText(r)
                            ? ((r.value as string | null) ?? '')
                            : JSON.stringify(r.value, null, 2),
                        )
                      )}
                    >
                      Sửa
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />
      <Modal
        open={!!editing}
        title={editing?.key}
        onCancel={() => setEditing(null)}
        onOk={() => void save()}
        okText="Lưu"
        width={editing && isText(editing) ? 720 : 520}
      >
        <Typography.Paragraph type="secondary">{editing?.description}</Typography.Paragraph>
        <Input.TextArea
          rows={isText(editing ?? ({ key: '', value: null } as Setting)) ? 10 : 8}
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{
            fontFamily: editing && isText(editing) ? undefined : 'monospace',
          }}
        />
      </Modal>
    </Card>
  );
}
